import { useState } from "react";
import { motion } from "framer-motion";
import { Crown, ArrowRight } from "lucide-react";
import { Screen, Spot, Hotspot } from "@/components/site/Screen";
import { NavSpots } from "@/components/site/ArtPage";
import { RulesDialog } from "@/components/site/RulesDialog";
import { useIsMobile } from "@/lib/hooks";
import { useBooking, isLux } from "@/components/booking/useBooking";
import { ServiceField, TypeButton, DateField, TimeField, PhotoField, NotesField, AgreeBox, InfoField, LuxSelect, typeProps } from "@/components/booking/fields";
import { SuccessDialog } from "@/components/booking/SuccessDialog";
import { ProcessCards } from "@/components/booking/ProcessCards";

const IN_STUDIO = "In-Studio (Washington, DC)";
const MOBILE = "Mobile (travel fees apply)";
const CROP_BG = { backgroundImage: "url(/assets/booking.jpg)" };

const rise = (i) => ({
  initial: { y: 10, opacity: 0.4 }, animate: { y: 0, opacity: 1 },
  transition: { delay: 0.35 + i * 0.07, duration: 0.6, ease: [0.16, 1, 0.3, 1] },
});

const OverlayForm = ({ b, openRules }) => {
  const lux = isLux(b.form.service);
  return (
    <>
      <Spot box={[11.55, 36.4, 45.15, 2.85]}><motion.div className="fill" {...rise(0)}><ServiceField b={b} cls="ov-control" /></motion.div></Spot>
      <Spot box={[11.6, 42.65, 21.95, 4.4]}><TypeButton b={b} value={IN_STUDIO} cls="ov-type" {...typeProps.studio} /></Spot>
      <Spot box={[34.75, 42.65, 21.95, 4.4]}><TypeButton b={b} value={MOBILE} cls="ov-type" {...typeProps.mobile} /></Spot>
      <Spot box={[11.8, 50.4, 44.95, 2.85]}><motion.div className="fill" {...rise(2)}><DateField b={b} cls="ov-control" /></motion.div></Spot>
      <Spot box={[11.8, 56.6, 44.95, 2.85]}><motion.div className="fill" {...rise(3)}><TimeField b={b} cls="ov-control" /></motion.div></Spot>
      <Spot box={[11.7, 62.9, 44.8, 11.15]}><PhotoField b={b} cls="ov-drop" /></Spot>
      <Spot box={[11.8, 77.8, 44.9, 4.35]}><motion.div className="fill" {...rise(5)}><NotesField b={b} cls="ov-control" lux={lux} /></motion.div></Spot>
      <Hotspot x={11.9} y={83.3} w={40} h={2.5} label="Toggle House Rules agreement" testId="booking-agree-text"
        onClick={() => b.set("agree", !b.form.agree)} />
      <Spot box={[8.45, 83.65, 2.05, 1.4]}><AgreeBox b={b} cls="ov-check" /></Spot>
      <Hotspot x={33.4} y={83.25} w={4.3} h={1.3} label="Read the House Rules" testId="booking-rules-link" onClick={openRules} />
      <Spot box={[11.8, 86.85, 40.6, 3.35]}>
        <motion.button type="button" className="ov-submit" onClick={b.startSubmit} whileTap={{ scale: 0.97 }}
          aria-label="Submit booking request" data-testid="booking-submit-button" />
      </Spot>
      <motion.aside className="info-panel" initial={{ y: 12, opacity: 0.4 }} animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.4, duration: 0.6, ease: [0.16, 1, 0.3, 1] }} data-testid="booking-info-panel">
        <h2 className="info-title"><Crown /> Your Info</h2>
        <InfoField label="Full name *" cls="panel-control" value={b.form.name} error={b.errors.name}
          onChange={(e) => b.set("name", e.target.value)} placeholder="First & last" autoComplete="name"
          testId="booking-name-input" />
        <InfoField label="Email *" cls="panel-control" type="email" value={b.form.email} error={b.errors.email}
          onChange={(e) => b.set("email", e.target.value)} placeholder="you@email.com" autoComplete="email"
          testId="booking-email-input" />
        <InfoField label="Phone (texts) *" cls="panel-control" type="tel" value={b.form.phone} error={b.errors.phone}
          onChange={(e) => b.set("phone", e.target.value)} placeholder="(202) 555-0123" autoComplete="tel"
          testId="booking-phone-input" />
        {lux ? <LuxSelect b={b} cls="panel-control" /> : null}
        <p className="info-note deposit-note">A $15 nonrefundable deposit (applied to your service total) is due after Smiless approves your request. Mobile travel fees are separate. This sends a request — it doesn't confirm your appointment yet.</p>
        <motion.button type="button" className="cta w-full" onClick={b.startSubmit} whileTap={{ scale: 0.97 }}
          disabled={b.submitting} data-testid="booking-send-panel-button">
          {b.submitting ? "Sending your request…" : <>Send Booking Request <ArrowRight size={17} /></>}
        </motion.button>
      </motion.aside>
    </>
  );
};

const Step = ({ n, label, children }) => (
  <div className="st-step">
    <div className="st-step-head"><span className="st-num">{n}</span>{label}</div>
    {children}
  </div>
);

const StackedForm = ({ b, openRules }) => {
  const lux = isLux(b.form.service);
  return (
    <div className="bk-stack" data-testid="booking-form-mobile">
      <h2 className="st-title"><Crown /> Book Your Appointment</h2>
      <Step n={1} label="Select your service *"><ServiceField b={b} cls="st-control" /></Step>
      <Step n={2} label="Choose your appointment type *">
        <div className="st-types">
          <TypeButton b={b} value={IN_STUDIO} cls="st-type" {...typeProps.studio} />
          <TypeButton b={b} value={MOBILE} cls="st-type" {...typeProps.mobile} />
        </div>
      </Step>
      <Step n={3} label="Pick a date *"><DateField b={b} cls="st-control" /></Step>
      <Step n={4} label="Select a time *"><TimeField b={b} cls="st-control" /></Step>
      <Step n={5} label="Upload hair photos *"><PhotoField b={b} cls="st-drop" showPrompt /></Step>
      <Step n={6} label={lux ? "Notes (required for Lux Style) *" : "Additional notes (optional)"}>
        <NotesField b={b} cls="st-control st-textarea" lux={lux} />
      </Step>
      {lux ? <Step n={7} label="Choose your Lux Style *"><LuxSelect b={b} cls="st-control" /></Step> : null}
      <Step n={lux ? 8 : 7} label="Your info *">
        <div className="st-grid2">
          <InfoField label="Full name" cls="st-control" value={b.form.name} error={b.errors.name}
            onChange={(e) => b.set("name", e.target.value)} placeholder="First & last" autoComplete="name"
            testId="booking-name-input" />
          <InfoField label="Email" cls="st-control" type="email" value={b.form.email} error={b.errors.email}
            onChange={(e) => b.set("email", e.target.value)} placeholder="you@email.com" autoComplete="email"
            testId="booking-email-input" />
        </div>
        <InfoField label="Phone (texts)" cls="st-control" type="tel" value={b.form.phone} error={b.errors.phone}
          onChange={(e) => b.set("phone", e.target.value)} placeholder="(202) 555-0123" autoComplete="tel"
          testId="booking-phone-input" />
      </Step>
      <div className="st-agree">
        <AgreeBox b={b} cls="st-check" />
        <p>I have read and agree to the <button type="button" onClick={openRules} data-testid="booking-rules-link-mobile">House Rules</button>, including the deposit, cancellation, and late policy. *</p>
      </div>
      <p className="info-note">A $15 nonrefundable deposit (applied to your service total) is due after Smiless approves your request. Mobile travel fees are separate. This sends a request — it doesn't confirm your appointment yet.</p>
      <motion.button type="button" className="cta w-full st-submit" onClick={b.startSubmit} whileTap={{ scale: 0.97 }}
        disabled={b.submitting} data-testid="booking-submit-button-mobile">
        {b.submitting ? "Sending your request…" : <><Crown size={18} /> Submit Booking Request <ArrowRight size={18} /></>}
      </motion.button>
    </div>
  );
};

export default function BookingPage({ go }) {
  const b = useBooking();
  const mobile = useIsMobile();
  const [rulesOpen, setRulesOpen] = useState(false);
  const openRules = () => setRulesOpen(true);
  return (
    <section aria-label="VIP Booking Lounge">
      {mobile ? (
        <>
          <div className="crop crop-hero" style={CROP_BG} role="img" aria-label="Your Throne Is Waiting, Boo. Book your appointment." />
          <StackedForm b={b} openRules={openRules} />
        </>
      ) : (
        <Screen page="booking">
          <NavSpots page="booking" go={go} />
          <OverlayForm b={b} openRules={openRules} />
        </Screen>
      )}
      <ProcessCards />
      <RulesDialog open={rulesOpen} onOpenChange={setRulesOpen}
        onAgree={() => { b.set("agree", true); setRulesOpen(false); }} />
      <SuccessDialog b={b} go={go} />
    </section>
  );
}
